import type {
  NotificationConsentChannel,
  NotificationConsentFamily,
  NotificationConsentRow,
} from '@/types/qbot';

/**
 * La pagina «Notifiche» (impostazioni, L-2500 — famiglia «amici»).
 *
 * Il path sta qui e non ripetuto: la riga in Amici (`Link`) e la rotta (`router.tsx`) lo leggono
 * dallo stesso posto (stessa regola di `lib/qsocials/bloccati.ts`). Il router vuole il path
 * relativo, senza la barra iniziale.
 */
export const NOTIFICHE_PATH = '/friends/notifications';
export const NOTIFICHE_ROUTE = NOTIFICHE_PATH.slice(1);

/**
 * Le famiglie che la pagina SA disegnare, nell'ordine in cui le mostra. `system` non c'e' mai.
 * `partite` (QBot #265, L-2924) subito dopo `amici`: sono le due famiglie «di gioco», e come `amici` compare solo quando il
 * server la manda (chiave `notif.partiteEnabled` accesa).
 */
// `segui-live` (QBot #266, r38) dopo `partite`: e' la terza famiglia «di gioco». E' opt-in, quindi questa pagina e' l'UNICO posto
// in cui un utente la puo' accendere: una famiglia che la pagina scartasse resterebbe spenta per tutti.
const FAMIGLIE_MOSTRATE: readonly NotificationConsentFamily[] = ['amici', 'partite', 'segui-live', 'reminder', 'promotional'];

export interface GruppoConsenso {
  family: NotificationConsentFamily;
  righe: NotificationConsentRow[];
}

/**
 * Raggruppa le righe del server per famiglia. Regole:
 *  · una famiglia che il server NON manda (`amici` con l'interruttore spento) non ha gruppo: niente
 *    testata vuota, niente «buco»;
 *  · una famiglia che la pagina non conosce (o `system`, che il server non manda ma la pagina non
 *    dovrebbe mai poter spegnere) viene scartata, non disegnata con un'etichetta inventata;
 *  · l'ordine dei canali dentro il gruppo e' quello del server (il freno: telegram, discord).
 */
export function raggruppaPerFamiglia(righe: readonly NotificationConsentRow[]): GruppoConsenso[] {
  return FAMIGLIE_MOSTRATE.map((family) => ({
    family,
    righe: righe.filter((r) => r.family === family),
  })).filter((g) => g.righe.length > 0);
}

/** La chiave stabile di una riga: la stessa per il `key` di React e per il `data-testid`. */
export const chiaveRiga = (r: Pick<NotificationConsentRow, 'family' | 'channel'>): string =>
  `${r.family}-${r.channel}`;

/**
 * Perche' una riga `locked` non si preme, come chiave i18n (`general:notifiche.locked_*`).
 *  · promozionale su Telegram spento: «si attiva con un consenso dedicato» (ADR-0084 §5, l'opt-in
 *    vuole finalita' e frequenza, che questa pagina non porta);
 *  · qualunque cosa su Discord: «non disponibile su Discord» (§4 e §6-septies: chiuso nel codice);
 *  · il resto: la frase generica. Il server e' l'unica fonte di `locked`, ma il PERCHE' lo dice la pagina.
 */
export function motivoBloccata(
  family: NotificationConsentFamily,
  channel: NotificationConsentChannel,
): 'locked_optin' | 'locked_discord' | 'locked_generic' {
  if (family === 'promotional' && channel === 'telegram') return 'locked_optin';
  if (channel === 'discord') return 'locked_discord';
  return 'locked_generic';
}

/**
 * Da un errore di RTK Query alla chiave i18n della frase che l'utente legge
 * (`general:notifiche.err_*`). 409 e 400 hanno una frase loro: non sono «errore generico».
 * `message` e' il codice macchina di QBot (`consent_locked`, `optin_requires_declaration`,
 * `family_disabled`, ...), non testo per persone: la pagina lo traduce, non lo stampa.
 */
export function chiaveErrore(error: unknown): 'err_locked' | 'err_optin' | 'err_unavailable' | 'err_generic' {
  const e = error as { status?: unknown; data?: { error?: { message?: unknown } } } | undefined;
  const status = e?.status;
  const codice = e?.data?.error?.message;
  if (status === 409) return codice === 'optin_requires_declaration' ? 'err_optin' : 'err_locked';
  if (status === 400) return 'err_unavailable';
  return 'err_generic';
}

/** Vero se l'errore dice che la lista che la pagina mostra non e' piu' quella del server (400/409). */
export function listaSuperata(error: unknown): boolean {
  const status = (error as { status?: unknown } | undefined)?.status;
  return status === 409 || status === 400;
}
